/**
 * Litter Management Component
 * Full lifecycle: Birth registration → Kitten tracking → Weaning → Sales
 */

import { useState, useEffect } from 'react';
import { 
  Baby,
  Plus,
  Calendar,
  Scale,
  Clock,
  CheckCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Camera,
  DollarSign,
  User,
  Phone,
  Mail,
  ChevronRight,
  Eye,
  Heart
} from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Select, Alert, Tabs } from '../shared/UIComponents';
import { formatCurrency, formatDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { getCatsByCattery, createCat, SA_CAT_BREEDS } from '../../firebase/firestore';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Kitten development stages
const DEVELOPMENT_STAGES = {
  newborn: { label: 'Newborn', days: [0, 14], color: 'bg-pink-100 text-pink-700' },
  eyes_open: { label: 'Eyes Opening', days: [14, 21], color: 'bg-purple-100 text-purple-700' },
  walking: { label: 'Walking', days: [21, 35], color: 'bg-blue-100 text-blue-700' },
  weaning: { label: 'Weaning', days: [35, 56], color: 'bg-amber-100 text-amber-700' },
  socialization: { label: 'Socialization', days: [56, 84], color: 'bg-teal-100 text-teal-700' },
  ready: { label: 'Ready for Home', days: [84, 999], color: 'bg-paw-100 text-paw-700' }
};

// Feeding schedule by age
const FEEDING_SCHEDULES = {
  '0-2': { weeks: '0-2 weeks', frequency: 'Every 2-3 hours', type: 'Mother\'s milk / KMR formula', notes: 'Round the clock feeding if bottle fed' },
  '2-4': { weeks: '2-4 weeks', frequency: 'Every 4-6 hours', type: 'Mother\'s milk + introduction to gruel', notes: 'Start weaning process' },
  '4-6': { weeks: '4-6 weeks', frequency: '4 times daily', type: 'Wet kitten food gruel', notes: 'Gradual transition from milk' },
  '6-8': { weeks: '6-8 weeks', frequency: '3-4 times daily', type: 'Wet food + kibble introduced', notes: 'Should be mostly weaned' },
  '8-12': { weeks: '8-12 weeks', frequency: '3 times daily', type: 'Kitten food (wet + dry)', notes: 'Ready for new homes after 12 weeks' },
  '12+': { weeks: '12+ weeks', frequency: '2-3 times daily', type: 'Quality kitten food', notes: 'Continue kitten food until 1 year' }
};

export const LitterManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [litters, setLitters] = useState([]);
  const [breedingPairs, setBreedingPairs] = useState([]);
  const [cats, setCats] = useState([]);
  const [showNewLitterModal, setShowNewLitterModal] = useState(false);
  const [showKittenModal, setShowKittenModal] = useState(false);
  const [selectedLitter, setSelectedLitter] = useState(null);
  const [selectedKitten, setSelectedKitten] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (user?.catteryId) {
      loadData();
    }
  }, [user?.catteryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const catsData = await getCatsByCattery(user.catteryId);
      setCats(catsData);
      
      const pairsQuery = query(
        collection(db, 'breeding_pairs'),
        where('catteryId', '==', user.catteryId)
      );
      const pairsSnap = await getDocs(pairsQuery);
      setBreedingPairs(pairsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const littersQuery = query(
        collection(db, 'litters'),
        where('catteryId', '==', user.catteryId),
        orderBy('dateOfBirth', 'desc')
      );
      const littersSnap = await getDocs(littersQuery);
      const littersData = littersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      for (let litter of littersData) {
        const kittensQuery = query(
          collection(db, 'kittens'),
          where('litterId', '==', litter.id)
        );
        const kittensSnap = await getDocs(kittensQuery);
        litter.kittens = kittensSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      setLitters(littersData);
      
    } catch (error) {
      console.error('Error loading litter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeLitters = litters.filter(l => {
    const dob = l.dateOfBirth?.toDate ? l.dateOfBirth.toDate() : new Date(l.dateOfBirth);
    const ageInDays = Math.floor((new Date() - dob) / (1000 * 60 * 60 * 24));
    return ageInDays < 120;
  });
  
  const pastLitters = litters.filter(l => !activeLitters.includes(l));
  const getCat = (id) => cats.find(c => c.id === id);

  const tabs = [
    { id: 'active', label: 'Active Litters', count: activeLitters.length },
    { id: 'past', label: 'Past Litters', count: pastLitters.length },
    { id: 'feeding', label: 'Feeding Guide' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Litter Management</h1>
          <p className="text-gray-500">Track litters from birth to new homes</p>
        </div>
        <Button onClick={() => setShowNewLitterModal(true)}>
          <Plus className="w-4 h-4" />
          Register New Litter
        </Button>
      </div>

      {breedingPairs.filter(p => p.status === 'pregnant').length > 0 && (
        <Alert type="info" title="Expecting Litters">
          <p className="text-sm">
            You have {breedingPairs.filter(p => p.status === 'pregnant').length} pregnant queen(s). 
            Register the litter once kittens are born.
          </p>
        </Alert>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
            <Baby className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active Litters</p>
            <p className="text-2xl font-bold text-gray-900">{activeLitters.length}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Heart className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Kittens</p>
            <p className="text-2xl font-bold text-gray-900">
              {activeLitters.reduce((sum, l) => sum + (l.kittens?.length || 0), 0)}
            </p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-paw-100 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-paw-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Available</p>
            <p className="text-2xl font-bold text-gray-900">
              {activeLitters.reduce((sum, l) => 
                sum + (l.kittens?.filter(k => k.status === 'available').length || 0), 0
              )}
            </p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Reserved/Sold</p>
            <p className="text-2xl font-bold text-gray-900">
              {activeLitters.reduce((sum, l) => 
                sum + (l.kittens?.filter(k => ['reserved', 'sold'].includes(k.status)).length || 0), 0
              )}
            </p>
          </div>
        </Card>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'feeding' ? (
        <FeedingScheduleGuide />
      ) : (
        <LittersList 
          litters={activeTab === 'active' ? activeLitters : pastLitters}
          loading={loading}
          getCat={getCat}
          onAddKitten={(litter) => {
            setSelectedLitter(litter);
            setShowKittenModal(true);
          }}
          onEditKitten={(kitten, litter) => {
            setSelectedLitter(litter);
            setSelectedKitten(kitten);
            setShowKittenModal(true);
          }}
          onRefresh={loadData}
        />
      )}

      <NewLitterModal
        isOpen={showNewLitterModal}
        onClose={() => setShowNewLitterModal(false)}
        breedingPairs={breedingPairs}
        cats={cats}
        catteryId={user?.catteryId}
        onSuccess={() => {
          setShowNewLitterModal(false);
          loadData();
        }}
      />

      <KittenModal
        isOpen={showKittenModal}
        onClose={() => {
          setShowKittenModal(false);
          setSelectedLitter(null);
          setSelectedKitten(null);
        }}
        kitten={selectedKitten}
        litter={selectedLitter}
        catteryId={user?.catteryId}
        onSuccess={() => {
          setShowKittenModal(false);
          setSelectedLitter(null);
          setSelectedKitten(null);
          loadData();
        }}
      />
    </div>
  );
};

const LittersList = ({ litters, loading, getCat, onAddKitten, onEditKitten, onRefresh }) => {
  if (loading) {
    return <div className="space-y-4">
      {[...Array(2)].map((_, i) => (
        <Card key={i} className="animate-pulse"><div className="h-32 bg-gray-100 rounded-lg" /></Card>
      ))}
    </div>;
  }

  if (litters.length === 0) {
    return (
      <Card className="text-center py-12">
        <Baby className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Litters</h3>
        <p className="text-gray-500">Register a new litter when your queen gives birth.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {litters.map((litter) => (
        <LitterCard 
          key={litter.id}
          litter={litter}
          getCat={getCat}
          onAddKitten={() => onAddKitten(litter)}
          onEditKitten={(kitten) => onEditKitten(kitten, litter)}
          onRefresh={onRefresh}
        />
      ))}
    </div>
  );
};

const LitterCard = ({ litter, getCat, onAddKitten, onEditKitten, onRefresh }) => {
  const sire = getCat(litter.sireId);
  const dam = getCat(litter.damId);
  const dob = litter.dateOfBirth?.toDate ? litter.dateOfBirth.toDate() : new Date(litter.dateOfBirth);
  const ageInDays = Math.floor((new Date() - dob) / (1000 * 60 * 60 * 24));
  const ageInWeeks = Math.floor(ageInDays / 7);
  
  const getCurrentStage = () => {
    for (const [key, stage] of Object.entries(DEVELOPMENT_STAGES)) {
      if (ageInDays >= stage.days[0] && ageInDays < stage.days[1]) {
        return { key, ...stage };
      }
    }
    return { key: 'ready', ...DEVELOPMENT_STAGES.ready };
  };
  
  const stage = getCurrentStage();
  const kittens = litter.kittens || [];

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center">
            <Baby className="w-8 h-8 text-pink-500" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-gray-900">
              {litter.name || `${dam?.name || 'Unknown'}'s Litter`}
            </h3>
            <p className="text-sm text-gray-500">{sire?.name || 'Unknown'} × {dam?.name || 'Unknown'}</p>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-gray-400">Born: {formatDate(dob)}</span>
              <span className="text-xs text-gray-400">Age: {ageInWeeks} weeks ({ageInDays} days)</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={stage.color}>{stage.label}</Badge>
          <Button size="sm" onClick={onAddKitten}>
            <Plus className="w-4 h-4" />
            Add Kitten
          </Button>
        </div>
      </div>

      {kittens.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-4">
          {kittens.map((kitten) => (
            <KittenCard 
              key={kitten.id}
              kitten={kitten}
              ageInDays={ageInDays}
              onEdit={() => onEditKitten(kitten)}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>No kittens registered yet. Click "Add Kitten" to register newborns.</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100">
        <FeedingReminder ageInWeeks={ageInWeeks} />
      </div>
    </Card>
  );
};

const KittenCard = ({ kitten, ageInDays, onEdit, onRefresh }) => {
  const statusColors = {
    keeping: 'bg-purple-100 text-purple-700',
    available: 'bg-paw-100 text-paw-700',
    reserved: 'bg-amber-100 text-amber-700',
    sold: 'bg-blue-100 text-blue-700',
    deceased: 'bg-gray-100 text-gray-500'
  };

  const updateStatus = async (newStatus) => {
    try {
      await updateDoc(doc(db, 'kittens', kitten.id), { 
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      onRefresh();
    } catch (error) {
      console.error('Error updating kitten status:', error);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
      <div className="relative aspect-square rounded-lg bg-gray-200 mb-3 overflow-hidden">
        {kitten.photo ? (
          <img src={kitten.photo} alt={kitten.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">🐱</div>
        )}
        <div className="absolute top-2 right-2">
          <Badge className={statusColors[kitten.status] || statusColors.available}>{kitten.status}</Badge>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">{kitten.name || 'Unnamed'}</h4>
          <span className={cn(
            'w-5 h-5 rounded-full text-xs flex items-center justify-center',
            kitten.sex === 'male' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'
          )}>
            {kitten.sex === 'male' ? '♂' : '♀'}
          </span>
        </div>
        
        <p className="text-sm text-gray-500">{kitten.color || 'Color TBD'}</p>
        
        {kitten.price && (
          <p className="font-bold text-feline-600">{formatCurrency(kitten.price)}</p>
        )}

        {kitten.currentWeight && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Scale className="w-3 h-3" />{kitten.currentWeight}g
          </div>
        )}

        {kitten.buyerName && ['reserved', 'sold'].includes(kitten.status) && (
          <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
            <p className="flex items-center gap-1"><User className="w-3 h-3" />{kitten.buyerName}</p>
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <select
            value={kitten.status}
            onChange={(e) => updateStatus(e.target.value)}
            className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5"
          >
            <option value="keeping">Keeping</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="sold">Sold</option>
            <option value="deceased">Deceased</option>
          </select>
          <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-feline-600 rounded-lg hover:bg-white">
            <Edit className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const FeedingReminder = ({ ageInWeeks }) => {
  let scheduleKey = '12+';
  if (ageInWeeks < 2) scheduleKey = '0-2';
  else if (ageInWeeks < 4) scheduleKey = '2-4';
  else if (ageInWeeks < 6) scheduleKey = '4-6';
  else if (ageInWeeks < 8) scheduleKey = '6-8';
  else if (ageInWeeks < 12) scheduleKey = '8-12';
  
  const schedule = FEEDING_SCHEDULES[scheduleKey];

  return (
    <div className="bg-amber-50 rounded-lg p-3">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">🍼</div>
        <div className="flex-1">
          <p className="font-medium text-amber-900 text-sm">Feeding Schedule ({schedule.weeks})</p>
          <p className="text-xs text-amber-700 mt-1"><strong>{schedule.frequency}</strong> • {schedule.type}</p>
          <p className="text-xs text-amber-600 mt-1">{schedule.notes}</p>
        </div>
      </div>
    </div>
  );
};

const FeedingScheduleGuide = () => {
  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4">Kitten Feeding Guide</h3>
      <div className="space-y-4">
        {Object.entries(FEEDING_SCHEDULES).map(([key, schedule]) => (
          <div key={key} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-20 text-center">
              <p className="font-bold text-feline-600">{schedule.weeks}</p>
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{schedule.frequency}</p>
              <p className="text-sm text-gray-600">{schedule.type}</p>
              <p className="text-xs text-gray-500 mt-1">{schedule.notes}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">💡 Tips</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Always provide fresh water from 4 weeks onwards</li>
          <li>• Weigh kittens daily for the first 2 weeks</li>
          <li>• Use kitten-specific food, not adult cat food</li>
          <li>• Consult your vet if a kitten isn't gaining weight</li>
        </ul>
      </div>
    </Card>
  );
};

const NewLitterModal = ({ isOpen, onClose, breedingPairs, cats, catteryId, onSuccess }) => {
  const [formData, setFormData] = useState({
    breedingPairId: '',
    sireId: '',
    damId: '',
    dateOfBirth: new Date().toISOString().split('T')[0],
    name: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (formData.breedingPairId) {
      const pair = breedingPairs.find(p => p.id === formData.breedingPairId);
      if (pair) {
        setFormData(prev => ({ ...prev, sireId: pair.sireId, damId: pair.damId }));
      }
    }
  }, [formData.breedingPairId]);

  const handleSubmit = async () => {
    if (!formData.sireId || !formData.damId || !formData.dateOfBirth) return;
    try {
      setSubmitting(true);
      await addDoc(collection(db, 'litters'), {
        sireId: formData.sireId,
        damId: formData.damId,
        breedingPairId: formData.breedingPairId || null,
        dateOfBirth: Timestamp.fromDate(new Date(formData.dateOfBirth)),
        name: formData.name,
        notes: formData.notes,
        catteryId,
        createdAt: serverTimestamp()
      });
      if (formData.breedingPairId) {
        await updateDoc(doc(db, 'breeding_pairs', formData.breedingPairId), {
          status: 'delivered',
          actualDeliveryDate: Timestamp.fromDate(new Date(formData.dateOfBirth)),
          updatedAt: serverTimestamp()
        });
      }
      onSuccess();
    } catch (error) {
      console.error('Error creating litter:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const pregnantPairs = breedingPairs.filter(p => p.status === 'pregnant');
  const males = cats.filter(c => c.sex === 'male');
  const females = cats.filter(c => c.sex === 'female');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Register New Litter" size="md">
      <div className="space-y-4">
        {pregnantPairs.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">From Breeding Pair (Optional)</label>
            <select
              value={formData.breedingPairId}
              onChange={(e) => setFormData({ ...formData, breedingPairId: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
            >
              <option value="">Select from pregnant pairs...</option>
              {pregnantPairs.map(pair => {
                const sire = cats.find(c => c.id === pair.sireId);
                const dam = cats.find(c => c.id === pair.damId);
                return <option key={pair.id} value={pair.id}>{sire?.name} × {dam?.name}</option>;
              })}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Select label="Sire (Father) *" value={formData.sireId} onChange={(e) => setFormData({ ...formData, sireId: e.target.value })} options={males.map(c => ({ value: c.id, label: c.name }))} placeholder="Select sire" disabled={!!formData.breedingPairId} />
          <Select label="Dam (Mother) *" value={formData.damId} onChange={(e) => setFormData({ ...formData, damId: e.target.value })} options={females.map(c => ({ value: c.id, label: c.name }))} placeholder="Select dam" disabled={!!formData.breedingPairId} />
        </div>
        <Input label="Date of Birth *" type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })} />
        <Input label="Litter Name (Optional)" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Spring 2026 Litter" />
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting}>Register Litter</Button>
        </div>
      </div>
    </Modal>
  );
};

const KittenModal = ({ isOpen, onClose, kitten, litter, catteryId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', sex: '', color: '', ems: '', status: 'keeping', price: '', currentWeight: '', microchip: '',
    buyerName: '', buyerEmail: '', buyerPhone: '', depositPaid: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const isEditing = !!kitten;

  useEffect(() => {
    if (kitten) {
      setFormData({
        name: kitten.name || '', sex: kitten.sex || '', color: kitten.color || '', ems: kitten.ems || '',
        status: kitten.status || 'keeping', price: kitten.price || '', currentWeight: kitten.currentWeight || '',
        microchip: kitten.microchip || '', buyerName: kitten.buyerName || '', buyerEmail: kitten.buyerEmail || '',
        buyerPhone: kitten.buyerPhone || '', depositPaid: kitten.depositPaid || '', notes: kitten.notes || ''
      });
    } else {
      setFormData({ name: '', sex: '', color: '', ems: '', status: 'keeping', price: '', currentWeight: '', microchip: '', buyerName: '', buyerEmail: '', buyerPhone: '', depositPaid: '', notes: '' });
    }
  }, [kitten]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const data = {
        ...formData,
        price: formData.price ? Number(formData.price) : null,
        currentWeight: formData.currentWeight ? Number(formData.currentWeight) : null,
        depositPaid: formData.depositPaid ? Number(formData.depositPaid) : null,
        litterId: litter.id, sireId: litter.sireId, damId: litter.damId, dateOfBirth: litter.dateOfBirth,
        catteryId, updatedAt: serverTimestamp()
      };
      if (isEditing) {
        await updateDoc(doc(db, 'kittens', kitten.id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'kittens'), data);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving kitten:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Kitten' : 'Add Kitten'} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Kitten name" />
          <Select label="Sex *" value={formData.sex} onChange={(e) => setFormData({ ...formData, sex: e.target.value })} options={[{ value: 'male', label: 'Male ♂' }, { value: 'female', label: 'Female ♀' }]} placeholder="Select sex" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder="e.g., Black Classic Tabby" />
          <Input label="EMS Code" value={formData.ems} onChange={(e) => setFormData({ ...formData, ems: e.target.value })} placeholder="e.g., MCO n 22" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Select label="Status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} options={[{ value: 'keeping', label: 'Keeping' }, { value: 'available', label: 'Available' }, { value: 'reserved', label: 'Reserved' }, { value: 'sold', label: 'Sold' }, { value: 'deceased', label: 'Deceased' }]} />
          <Input label="Price (ZAR)" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="0" />
          <Input label="Weight (grams)" type="number" value={formData.currentWeight} onChange={(e) => setFormData({ ...formData, currentWeight: e.target.value })} placeholder="0" />
        </div>
        <Input label="Microchip Number" value={formData.microchip} onChange={(e) => setFormData({ ...formData, microchip: e.target.value })} placeholder="15-digit microchip" />
        {['reserved', 'sold'].includes(formData.status) && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Buyer Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Buyer Name" value={formData.buyerName} onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })} placeholder="Full name" />
              <Input label="Phone" value={formData.buyerPhone} onChange={(e) => setFormData({ ...formData, buyerPhone: e.target.value })} placeholder="082 123 4567" />
              <Input label="Email" value={formData.buyerEmail} onChange={(e) => setFormData({ ...formData, buyerEmail: e.target.value })} placeholder="email@example.com" />
              <Input label="Deposit Paid (ZAR)" type="number" value={formData.depositPaid} onChange={(e) => setFormData({ ...formData, depositPaid: e.target.value })} placeholder="0" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting}>{isEditing ? 'Update' : 'Add'} Kitten</Button>
        </div>
      </div>
    </Modal>
  );
};

export default LitterManager;
